import { User, DollarSign, Building2, CreditCard, FileText } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Withdrawal, WithdrawalStatus } from '@/types';

interface WithdrawalDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  withdrawal: Withdrawal | null;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onMarkPaid?: (id: string) => void;
  isLoading?: boolean;
}

export default function WithdrawalDetailsModal({
  isOpen,
  onClose,
  withdrawal,
  onApprove,
  onReject,
  onMarkPaid,
  isLoading = false,
}: WithdrawalDetailsModalProps) {
  if (!withdrawal) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: WithdrawalStatus) => {
    switch (status) {
      case WithdrawalStatus.PENDING:
        return 'bg-yellow-50 text-yellow-700 border-yellow-100';
      case WithdrawalStatus.APPROVED:
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case WithdrawalStatus.PROCESSING:
        return 'bg-purple-50 text-purple-700 border-purple-100';
      case WithdrawalStatus.COMPLETED:
        return 'bg-green-50 text-green-700 border-green-100';
      case WithdrawalStatus.REJECTED:
        return 'bg-red-50 text-red-700 border-red-100';
      case WithdrawalStatus.FAILED:
        return 'bg-gray-50 text-gray-700 border-gray-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <div className="py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold text-gray-900">Withdrawal Details</h2>
              <Badge
                variant="outline"
                className={`${getStatusColor(withdrawal.status)} px-3 py-1 rounded-lg font-medium capitalize`}
              >
                {withdrawal.status}
              </Badge>
            </div>
            <p className="text-sm text-gray-600">Review withdrawal request details</p>
          </div>

          {/* Amount Card */}
          <div className="bg-gradient-to-br from-[#FF6B35]/10 to-orange-50 rounded-xl p-6 border border-orange-100 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#FF6B35]/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <p className="text-sm text-gray-600 font-medium">Withdrawal Amount</p>
            </div>
            <p className="text-4xl font-bold text-gray-900">
              {formatCurrency(withdrawal.amount)}
            </p>
          </div>

          {/* Agent Information */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Agent Information</h3>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Name</span>
                <span className="text-sm font-medium text-gray-900">
                  {withdrawal.user?.firstName} {withdrawal.user?.lastName}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Email</span>
                <span className="text-sm font-medium text-gray-900">{withdrawal.user?.email}</span>
              </div>
              {withdrawal.user?.phoneNumber && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Phone</span>
                  <span className="text-sm font-medium text-gray-900">{withdrawal.user.phoneNumber}</span>
                </div>
              )}
            </div>
          </div>

          {/* Bank Account Information */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Bank Account Details</h3>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Bank Name</span>
                <span className="text-sm font-medium text-gray-900">
                  {withdrawal.bankAccount?.bankName || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Account Number</span>
                <span className="text-sm font-medium text-gray-900">
                  {withdrawal.bankAccount?.accountNumber || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Account Name</span>
                <span className="text-sm font-medium text-gray-900">
                  {withdrawal.bankAccount?.accountName || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Withdrawal Information */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Withdrawal Information</h3>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Request Date</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatDateTime(withdrawal.createdAt)}
                </span>
              </div>
              {withdrawal.reviewedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Reviewed Date</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDateTime(withdrawal.reviewedAt)}
                  </span>
                </div>
              )}
              {withdrawal.processedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Processed Date</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDateTime(withdrawal.processedAt)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Reason/Notes */}
          {withdrawal.reason && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Reason</h3>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {withdrawal.reason}
                </p>
              </div>
            </div>
          )}

          {/* Review Notes */}
          {withdrawal.reviewNotes && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Review Notes</h3>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {withdrawal.reviewNotes}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="sticky bottom-0 bg-white pt-4 border-t border-gray-200 space-y-3">
            {withdrawal.status === WithdrawalStatus.PENDING && onApprove && onReject && (
              <>
                <Button
                  onClick={() => onApprove(withdrawal.id)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  disabled={isLoading}
                >
                  Approve Withdrawal
                </Button>
                <Button
                  onClick={() => onReject(withdrawal.id)}
                  variant="outline"
                  className="w-full border-red-600 text-red-600 hover:bg-red-50"
                  disabled={isLoading}
                >
                  Reject Withdrawal
                </Button>
              </>
            )}
            {withdrawal.status === WithdrawalStatus.APPROVED && onMarkPaid && (
              <Button
                onClick={() => onMarkPaid(withdrawal.id)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isLoading}
              >
                Mark as Completed
              </Button>
            )}
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full"
              disabled={isLoading}
            >
              Close
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
